<script lang="ts" setup>
import {toRef} from "vue";
import {useField} from "vee-validate";
import {joinURL} from "ufo";
import {mapNodes} from "~/utils/map-nodes";
import type {NodeBaseMixin} from "#magnolia-layer/types/magnolia-types";

type Option = {
  label: string;
  value: string;
};

const props = withDefaults(
  defineProps<{
    id?: string;
    name: string;
    label: string;
    labelVisible?: boolean;
    value?: string;
    options: Option[] | string;
    placeholder?: string;
    asRequired?: boolean;
    successMessage?: string;
    errorMessage?: string;
    rules?: string;
  }>(),
  {
    labelVisible: true,
  },
);

const name = toRef(props, "name");

const {
  value: inputValue,
  errorMessage,
  handleBlur,
  handleChange,
  errors,
  meta,
} = useField(name, props.rules, {
  initialValue: props.value,
});

const {t} = useI18n();

const reinterpolate = (errorMessage: string, named: Record<string, any>) => {
  const asResolved = t(errorMessage, named, {resolvedMessage: true});
  if (asResolved !== errorMessage) {
    return asResolved;
  }
  return t(errorMessage, named, {resolvedMessage: false});
};

type OptionsRoot = NodeBaseMixin<"mgnl:folder"> & {
  children: OptionNode[];
};

type OptionNode = NodeBaseMixin<"somfy:option"> & {
  title: string;
};

const runtimeConfig = useRuntimeConfig();

async function fetchOptions(id: string): Promise<Option[]> {
  const {data} = await useMagnoliaFetch<NodeBaseMixin<'mgnl:folder'>, Option[]>(
    joinURL(runtimeConfig.public.mgnl.apiOptions, id),
    {
      transform: (node): Option[] =>
      {
        const children = mapNodes<OptionsRoot>(node).children;
        if (children) {
          return children.map<Option>((child) => ({
            label: child.title,
            value: child.name,
          }))
        } else {
          return [] as Option[];
        }
      }
    },
  );
  if (data.value) {
    return data.value as Option[];
  }
  return [] as Option[];
}

const _options = ref<Option[]>([])

watch(() => props.options, async () => {
  if (typeof props.options === "string") {
    _options.value = await fetchOptions(props.options);
  } else {
    _options.value = props.options;
  }
}, { immediate: true });

const _placeholder = computed(() => props.placeholder?.concat(props.asRequired ? ' *' : ''));

</script>

<template>
  <div
    :class="{'has-error': !!errorMessage, success: meta.valid}"
    class="select-input">
    <label :for="id ? id : name" v-if="labelVisible"
      >{{ label }} <sup v-if="asRequired">*</sup></label
    >
    <select
      :id="id ? id : name"
      ref="select"
      :name="name"
      :value="inputValue"
      @blur="handleBlur"
      @input="handleChange">
      <option value="">{{ _placeholder }}</option>
      <option v-for="option in _options" :value="option.value">
        {{ option.label }}
      </option>
    </select>
    <div class="help-message text-sunrise-100" role="alert">
      <template v-if="errorMessage">{{
        reinterpolate(errorMessage, {label, name, inputValue})
      }}</template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.select-input {
  display: flex;
  flex-direction: column;

  label {
    font-weight: bold;
    sup {
      color: var(--color-accent);
    }
  }

  label,
  [role="alert"] {
    margin-left: calc(var(--spacing) / 2);
  }

  .help-message {
    font-size: 0.75em;
  }

  select {
    background-color: rgba(255, 255, 255, 0);
    min-height: 40px;
    border-radius: 0;
    @apply border;
    font: inherit;
    padding: calc(var(--spacing) / 2) var(--spacing);
    color: var(--color-white);

    &:focus-visible {
      @apply page-dark:outline-white page-light:outline-black;
      outline-style: solid;
      outline-width: 2px;
    }

    &::placeholder {
      @apply page-dark:text-white page-light:text-black;
    }
  }

  ul {
    padding: 0;
    margin: 0;
    list-style-type: "- ";
    list-style-position: inside;
  }
}
</style>
